import { isSteuerIdValid } from "validate-steuerid";
import z from "zod";

import { isValidCountryCode } from "@/v1/constants/countryNames";
import { CompanyType } from "./IUser.type";
import { FIRMS } from "@/modules/firms";

export enum SupportedLanguage {
  EN = "en",
  DE = "de",
}

export const isPhoneNumberValid = (phoneNumber: string): boolean =>
  /^\+[1-9]\d{6,14}$/.test(phoneNumber.replace(/\s/g, ""));

export const isPhoneNumberSupported = (phoneNumber: string) =>
  !phoneNumber.match(/^\+380/);

export const createUserSchema = z
  .object({
    email: z.string().email("Please enter a valid email address"),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    mobileNumber: z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        if (!val) return;

        if (!isPhoneNumberValid(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Please include country code (e.g., +49) and do not use dashes or spaces. Example: +4915112345678",
          });
        }
        if (!isPhoneNumberSupported(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Phone number not supported",
          });
        }
      }),
    language: z
      .nativeEnum(SupportedLanguage, {
        errorMap: () => ({ message: "Please select a valid language" }),
      })
      .optional()
      .default(SupportedLanguage.DE),
    street: z.string().optional(),
    postCode: z.string().optional(),
    city: z.string().optional(),
    country: z
      .string()
      .refine((val) => isValidCountryCode(val), {
        message: "Please select a valid country",
      })
      .optional()
      .default("DE"),
    deTaxId: z
      .string()
      .optional()
      .refine((val) => !val || isSteuerIdValid(val), {
        message: "Tax ID is not valid (11 digits, last one is a check digit)",
      }),
    birthDate: z.coerce
      .date()
      .refine((val) => val < new Date(), {
        message: "Birth date must be in the past",
      })
      .optional(),
    businessTradingName: z.string().optional(),
    businessTypeComment: z.string().optional(),
    preferredName: z.string().optional(),
    // Required since SMT-1203: the FsE form is chosen from the Rechtsform, so a
    // Mandant without one has no form to open.
    companyType: z.nativeEnum(CompanyType, {
      errorMap: () => ({ message: "Please select a Rechtsform" }),
    }),
    firm: z.enum(FIRMS).default("smarta"),
  })
  .superRefine((user, ctx) => {
    if (
      user.country === "DE" &&
      user.postCode &&
      !/^\d{5}$/.test(user.postCode)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Post code must be 5 digits",
        path: ["postCode"],
      });
    }
  });

export const createKontaxUserSchema = z.object({
  businessType: z.string().optional(),
});

export const createUserRequestDto = z.object({
  user: createUserSchema,
  kontaxUser: createKontaxUserSchema,
});

export type CreateUserRequestDto = z.infer<typeof createUserRequestDto>;
